import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import userApi from "../../api/user/user.api";
import { setUser } from "../../store/features/userSlice";
import { setAuthModalOpen } from "../../store/features/authModalSlice";
import { toast } from "react-hot-toast";
import ProcessingBar from "Util/Processingbar";
import LogoSignIn from "components/AuthModal/LogoSignIn";

const EmailStep = ({ email, setEmail, onContinue, isLoading }) => {
  return (
    <div>
      <div className="my-3 text-center text-navy">
        <h3 className="text-2xl font-bold text-navy">Sign in </h3>
        <p className="text-lg font-light mb-6">or create account</p>

        <p className="text-md">
          Enter your email to sign in or create an account
        </p>
      </div>

      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email address"
        className="w-full border rounded-lg px-4 py-3"
      />

      <button
        className="mt-6 w-full bg-gradient-to-r from-blue-800 to-navy py-3 rounded-lg font-semibold tracking-wide"
        onClick={onContinue}
      >
        {isLoading ? (
          <ProcessingBar />
        ) : (
          <p className="text-yellowstar animate-neonFlicker">Continue</p>
        )}{" "}
      </button>
    </div>
  );
};

const OtpStep = ({ email, otp, setOtp, verifyOtp, onBack, isLoading }) => {
  const inputsRef = useRef([]);

  const handleChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // move to next box
    if (value && index < 5) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData("text").trim();

    if (!/^\d{6}$/.test(pasteData)) return;

    const newOtp = pasteData.split("");
    setOtp(newOtp);

    // focus last input
    inputsRef.current[5].focus();
  };

  return (
    <div className="text-center space-y-6">
      <h2 className="text-2xl font-semibold">Check your messages</h2>

      <p className="text-gray-500 text-sm">
        Enter code sent to <strong>{email}</strong>
      </p>

      <div className="flex justify-center gap-3">
        {otp.map((digit, i) => (
          <input
            key={i}
            ref={(el) => (inputsRef.current[i] = el)}
            value={digit}
            maxLength={1}
            onPaste={handlePaste}
            onChange={(e) => handleChange(e.target.value, i)}
            className="w-12 h-12 border rounded-lg text-center text-lg focus:ring-2 focus:ring-yellowstar outline-none"
          />
        ))}
      </div>

      <button
        onClick={verifyOtp}
        className="w-full bg-blue-800 text-white py-3 rounded-lg"
      >
        {isLoading ? <ProcessingBar /> : "Verify"}
      </button>

      <button
        onClick={onBack}
        className="block w-full text-gray-600 hover:underline"
      >
        Use a different account
      </button>
    </div>
  );
};

const SignInSection = () => {
  const dispatch = useDispatch();

  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [isLoading, setIsLoading] = useState(false);

  const handleContinue = async () => {
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    try {
      setIsLoading(true);

      const { response, err } = await userApi.requestOtp({ email });

      if (err) {
        toast.error(err.message || "Failed to send OTP");
        return;
      }

      if (response) {
        toast.success("OTP sent to your email");
        setStep("otp");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async () => {
    const code = otp.join("");

    if (code.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }

    try {
      setIsLoading(true);

      const { response, err } = await userApi.verifyOtp({
        email,
        otp: code,
      });

      if (err) {
        toast.error(err.message || "Invalid OTP");
        return;
      }

      if (response) {
        toast.success("Login successful");

        dispatch(setUser(response.data.user));
        dispatch(setAuthModalOpen(false));
      }
    } catch (error) {
      console.error(error);
      toast.error("Verification failed");
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="max-w-md mx-auto">
      {step === "email" && (
        <EmailStep
          email={email}
          setEmail={setEmail}
          onContinue={handleContinue}
          isLoading={isLoading}
        />
      )}

      {step === "otp" && (
        <OtpStep
          email={email}
          otp={otp}
          setOtp={setOtp}
          verifyOtp={verifyOtp}
          onBack={() => setStep("email")}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default SignInSection;
